/**
 * Neon Battle Royale
 * Global storefront performance and motion engine
 * Version 3.0.0
 */

(() => {
  'use strict';

  const win = window;
  const doc = document;
  const root = doc.documentElement;
  const namespace = win.NBR || (win.NBR = {});

  if (
    namespace.engine &&
    typeof namespace.engine.destroy === 'function'
  ) {
    namespace.engine.destroy();
  }

  const ENGINE_VERSION = '3.0.0';
  const STORAGE_KEY = 'nbr-quality';

  const QUALITY_MODES = new Set([
    'ultra',
    'balanced',
    'performance'
  ]);

  const selectors = {
    reveal: '[data-nbr-reveal]',
    section: '[data-nbr-section]',
    video:
      'video[data-nbr-video], ' +
      '[data-nbr-video-wrapper] video'
  };

  const runtime =
    win.__NBR_RUNTIME__ || {};

  const mediaQueries = {
    reducedMotion: safeMatchMedia(
      '(prefers-reduced-motion: reduce)'
    ),
    coarsePointer: safeMatchMedia(
      '(pointer: coarse)'
    ),
    hoverPointer: safeMatchMedia(
      '(hover: hover) and (pointer: fine)'
    ),
    mobileViewport: safeMatchMedia(
      '(max-width: 749px)'
    )
  };

  const state = {
    started: false,
    destroyed: false,

    quality: 'balanced',
    qualitySource: 'auto',

    isVisible: !doc.hidden,
    isTouch: false,
    canHover: true,
    isMobile: false,
    reducedMotion: false,

    pointerX: win.innerWidth / 2,
    pointerY: win.innerHeight / 2,
    targetPointerX: win.innerWidth / 2,
    targetPointerY: win.innerHeight / 2,

    scrollY: 0,
    previousScrollY: 0,
    scrollProgress: 0,

    lastViewportWidth: 0,
    lastViewportHeight: 0,
    lastScrollYValue: null,
    lastScrollProgressValue: null,
    lastScrollDirection: '',
    lastScrolledState: '',

    pointerFrame: null,
    scrollFrame: null,
    resizeFrame: null,
    mutationFrame: null,
    idleTask: null,

    pendingMutationRoots: new Set()
  };

  const cleanupTasks = [];
  const observedRevealElements = new WeakSet();
  const observedSectionElements = new WeakSet();
  const observedVideoElements = new WeakSet();

  let revealObserver = null;
  let sectionObserver = null;
  let videoObserver = null;
  let mutationObserver = null;
  let engineApi = null;

  /* --------------------------------------------------------------------------
     Utilities
     -------------------------------------------------------------------------- */

  function safeMatchMedia(query) {
    try {
      return win.matchMedia(query);
    } catch (error) {
      return null;
    }
  }

  function clamp(value, minimum, maximum) {
    return Math.min(
      Math.max(value, minimum),
      maximum
    );
  }

  function safeStorageGet(key) {
    try {
      return win.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function safeStorageSet(key, value) {
    try {
      win.localStorage.setItem(key, value);
    } catch (error) {
      // Storage can be unavailable.
    }
  }

  function safeStorageRemove(key) {
    try {
      win.localStorage.removeItem(key);
    } catch (error) {
      // Storage can be unavailable.
    }
  }

  function dispatch(name, detail = {}) {
    try {
      win.dispatchEvent(
        new CustomEvent(name, {
          detail
        })
      );
    } catch (error) {
      // Custom events must never block the page.
    }
  }

  function listen(
    target,
    eventName,
    handler,
    options
  ) {
    if (
      !target ||
      typeof target.addEventListener !==
        'function'
    ) {
      return;
    }

    target.addEventListener(
      eventName,
      handler,
      options
    );

    cleanupTasks.push(() => {
      target.removeEventListener(
        eventName,
        handler,
        options
      );
    });
  }

  function listenToMediaQuery(
    mediaQuery,
    handler
  ) {
    if (!mediaQuery) {
      return;
    }

    if (
      typeof mediaQuery.addEventListener ===
      'function'
    ) {
      mediaQuery.addEventListener(
        'change',
        handler
      );

      cleanupTasks.push(() => {
        mediaQuery.removeEventListener(
          'change',
          handler
        );
      });

      return;
    }

    if (
      typeof mediaQuery.addListener ===
      'function'
    ) {
      mediaQuery.addListener(handler);

      cleanupTasks.push(() => {
        mediaQuery.removeListener(handler);
      });
    }
  }

  function getElements(
    container,
    selector
  ) {
    const elements = [];

    if (!container) {
      return elements;
    }

    if (
      container.nodeType === 1 &&
      typeof container.matches ===
        'function' &&
      container.matches(selector)
    ) {
      elements.push(container);
    }

    if (
      typeof container.querySelectorAll ===
      'function'
    ) {
      elements.push(
        ...container.querySelectorAll(
          selector
        )
      );
    }

    return elements;
  }

  function containsEngineElements(node) {
    if (
      !node ||
      (
        node.nodeType !== 1 &&
        node.nodeType !== 11
      )
    ) {
      return false;
    }

    if (
      node.nodeType === 1 &&
      typeof node.matches === 'function' &&
      (
        node.matches(selectors.reveal) ||
        node.matches(selectors.section) ||
        node.matches(selectors.video)
      )
    ) {
      return true;
    }

    if (
      typeof node.querySelector !==
      'function'
    ) {
      return false;
    }

    return Boolean(
      node.querySelector(
        [
          selectors.reveal,
          selectors.section,
          selectors.video
        ].join(',')
      )
    );
  }

  function supportsIntersectionObserver() {
    return 'IntersectionObserver' in win;
  }

  function cancelFrame(frameName) {
    if (state[frameName] === null) {
      return;
    }

    win.cancelAnimationFrame(
      state[frameName]
    );

    state[frameName] = null;
  }

  function cancelIdleTask() {
    if (state.idleTask === null) {
      return;
    }

    if (
      typeof win.cancelIdleCallback ===
      'function'
    ) {
      win.cancelIdleCallback(
        state.idleTask
      );
    } else {
      win.clearTimeout(state.idleTask);
    }

    state.idleTask = null;
  }

  function runWhenIdle(callback) {
    cancelIdleTask();

    if (
      typeof win.requestIdleCallback ===
      'function'
    ) {
      state.idleTask =
        win.requestIdleCallback(
          () => {
            state.idleTask = null;
            callback();
          },
          {
            timeout: 800
          }
        );

      return;
    }

    state.idleTask = win.setTimeout(
      () => {
        state.idleTask = null;
        callback();
      },
      120
    );
  }

  function setRootStyle(
    property,
    value
  ) {
    if (
      root.style.getPropertyValue(
        property
      ) === value
    ) {
      return;
    }

    root.style.setProperty(
      property,
      value
    );
  }

  function setRootDataset(
    property,
    value
  ) {
    if (
      root.dataset[property] === value
    ) {
      return;
    }

    root.dataset[property] = value;
  }

  function getScrollPosition() {
    const scrollingElement =
      doc.scrollingElement;

    if (scrollingElement) {
      return scrollingElement.scrollTop;
    }

    return win.pageYOffset || 0;
  }

  /* --------------------------------------------------------------------------
     Device and quality state
     -------------------------------------------------------------------------- */

  function updateDeviceState() {
    state.isTouch =
      Boolean(
        runtime.touchDevice
      ) ||
      Boolean(
        mediaQueries.coarsePointer &&
        mediaQueries.coarsePointer.matches
      );

    state.canHover =
      runtime.hoverDevice !== undefined
        ? Boolean(runtime.hoverDevice)
        : Boolean(
            mediaQueries.hoverPointer &&
            mediaQueries.hoverPointer.matches
          );

    state.isMobile =
      Boolean(
        runtime.mobileViewport
      ) ||
      Boolean(
        mediaQueries.mobileViewport &&
        mediaQueries.mobileViewport.matches
      );

    state.reducedMotion =
      Boolean(
        runtime.reducedMotion
      ) ||
      Boolean(
        mediaQueries.reducedMotion &&
        mediaQueries.reducedMotion.matches
      );

    setRootDataset(
      'nbrTouch',
      state.isTouch ? 'true' : 'false'
    );

    setRootDataset(
      'nbrHover',
      state.canHover ? 'true' : 'false'
    );

    setRootDataset(
      'nbrViewport',
      state.isMobile
        ? 'mobile'
        : 'desktop'
    );

    setRootDataset(
      'nbrMotion',
      state.reducedMotion
        ? 'reduced'
        : 'full'
    );
  }

  function updateViewportVariables() {
    const width = Math.max(
      win.innerWidth,
      1
    );

    const height = Math.max(
      win.innerHeight,
      1
    );

    if (
      width === state.lastViewportWidth &&
      height === state.lastViewportHeight
    ) {
      return;
    }

    state.lastViewportWidth = width;
    state.lastViewportHeight = height;

    setRootStyle(
      '--nbr-viewport-width',
      `${width}px`
    );

    setRootStyle(
      '--nbr-viewport-height',
      `${height}px`
    );

    setRootStyle(
      '--nbr-vh',
      `${height * 0.01}px`
    );
  }

  function getAutomaticQuality() {
    const preset =
      root.dataset.nbrQuality;

    if (
      preset === 'performance' ||
      runtime.lowPowerDevice
    ) {
      return 'performance';
    }

    if (
      state.reducedMotion ||
      runtime.reducedData
    ) {
      return 'performance';
    }

    /*
     * Balanced is the automatic maximum.
     * Ultra remains available as a manual option,
     * but will not be forced on normal devices.
     */
    return 'balanced';
  }

  function applyQuality(
    mode,
    {
      persist = false,
      source = 'auto'
    } = {}
  ) {
    if (!QUALITY_MODES.has(mode)) {
      return false;
    }

    const changed =
      state.quality !== mode ||
      state.qualitySource !== source;

    state.quality = mode;
    state.qualitySource = source;

    setRootDataset(
      'nbrQuality',
      mode
    );

    setRootDataset(
      'nbrQualitySource',
      source
    );

    if (persist) {
      safeStorageSet(
        STORAGE_KEY,
        mode
      );
    }

    if (
      mode === 'performance' ||
      state.reducedMotion ||
      state.isTouch
    ) {
      resetPointerPosition();
    }

    syncAllVideos();

    if (changed) {
      dispatch(
        'nbr:qualitychange',
        {
          quality: mode,
          source
        }
      );
    }

    return true;
  }

  function detectQualityMode() {
    const savedMode =
      safeStorageGet(STORAGE_KEY);

    if (
      QUALITY_MODES.has(savedMode)
    ) {
      applyQuality(
        savedMode,
        {
          source: 'manual'
        }
      );

      return;
    }

    applyQuality(
      getAutomaticQuality(),
      {
        source: 'auto'
      }
    );
  }

  /* --------------------------------------------------------------------------
     Pointer engine
     -------------------------------------------------------------------------- */

  function pointerEffectsEnabled() {
    return (
      state.isVisible &&
      state.canHover &&
      !state.isTouch &&
      !state.reducedMotion &&
      state.quality !== 'performance'
    );
  }

  function updatePointerVariables() {
    const smoothing =
      state.quality === 'ultra'
        ? 0.14
        : 0.2;

    state.pointerX +=
      (
        state.targetPointerX -
        state.pointerX
      ) * smoothing;

    state.pointerY +=
      (
        state.targetPointerY -
        state.pointerY
      ) * smoothing;

    const viewportWidth = Math.max(
      win.innerWidth,
      1
    );

    const viewportHeight = Math.max(
      win.innerHeight,
      1
    );

    const percentageX =
      clamp(
        state.pointerX /
          viewportWidth,
        0,
        1
      ) * 100;

    const percentageY =
      clamp(
        state.pointerY /
          viewportHeight,
        0,
        1
      ) * 100;

    const normalizedX =
      (percentageX - 50) / 50;

    const normalizedY =
      (percentageY - 50) / 50;

    setRootStyle(
      '--nbr-pointer-x',
      `${percentageX.toFixed(2)}%`
    );

    setRootStyle(
      '--nbr-pointer-y',
      `${percentageY.toFixed(2)}%`
    );

    setRootStyle(
      '--nbr-pointer-normalized-x',
      normalizedX.toFixed(4)
    );

    setRootStyle(
      '--nbr-pointer-normalized-y',
      normalizedY.toFixed(4)
    );
  }

  function renderPointerFrame() {
    state.pointerFrame = null;

    if (!pointerEffectsEnabled()) {
      return;
    }

    updatePointerVariables();

    const stillMoving =
      Math.abs(
        state.targetPointerX -
        state.pointerX
      ) > 0.35 ||
      Math.abs(
        state.targetPointerY -
        state.pointerY
      ) > 0.35;

    if (stillMoving) {
      requestPointerFrame();
    }
  }

  function requestPointerFrame() {
    if (
      state.pointerFrame !== null ||
      !pointerEffectsEnabled()
    ) {
      return;
    }

    state.pointerFrame =
      win.requestAnimationFrame(
        renderPointerFrame
      );
  }

  function handlePointerMove(event) {
    if (!pointerEffectsEnabled()) {
      return;
    }

    state.targetPointerX =
      event.clientX;

    state.targetPointerY =
      event.clientY;

    requestPointerFrame();
  }

  function handlePointerLeave() {
    state.targetPointerX =
      win.innerWidth / 2;

    state.targetPointerY =
      win.innerHeight / 2;

    requestPointerFrame();
  }

  function resetPointerPosition() {
    cancelFrame('pointerFrame');

    state.pointerX =
      win.innerWidth / 2;

    state.pointerY =
      win.innerHeight / 2;

    state.targetPointerX =
      state.pointerX;

    state.targetPointerY =
      state.pointerY;

    setRootStyle(
      '--nbr-pointer-x',
      '50%'
    );

    setRootStyle(
      '--nbr-pointer-y',
      '50%'
    );

    setRootStyle(
      '--nbr-pointer-normalized-x',
      '0'
    );

    setRootStyle(
      '--nbr-pointer-normalized-y',
      '0'
    );
  }

  /* --------------------------------------------------------------------------
     Scroll engine
     -------------------------------------------------------------------------- */

  function updateScrollState() {
    state.scrollFrame = null;

    const scrollingElement =
      doc.scrollingElement ||
      doc.documentElement;

    const currentScrollY =
      Math.max(
        getScrollPosition(),
        0
      );

    const scrollHeight = Math.max(
      scrollingElement.scrollHeight,
      doc.body
        ? doc.body.scrollHeight
        : 0
    );

    const maximumScroll = Math.max(
      scrollHeight - win.innerHeight,
      0
    );

    state.previousScrollY =
      state.scrollY;

    state.scrollY =
      currentScrollY;

    state.scrollProgress =
      maximumScroll > 0
        ? clamp(
            currentScrollY /
              maximumScroll,
            0,
            1
          )
        : 0;

    const roundedScrollY =
      Math.round(currentScrollY);

    const progressValue =
      state.scrollProgress.toFixed(4);

    if (
      state.lastScrollYValue !==
      roundedScrollY
    ) {
      state.lastScrollYValue =
        roundedScrollY;

      setRootStyle(
        '--nbr-scroll-y',
        `${roundedScrollY}px`
      );
    }

    if (
      state.lastScrollProgressValue !==
      progressValue
    ) {
      state.lastScrollProgressValue =
        progressValue;

      setRootStyle(
        '--nbr-scroll-progress',
        progressValue
      );
    }

    const scrolledState =
      currentScrollY > 8
        ? 'true'
        : 'false';

    if (
      state.lastScrolledState !==
      scrolledState
    ) {
      state.lastScrolledState =
        scrolledState;

      setRootDataset(
        'nbrScrolled',
        scrolledState
      );
    }

    const delta =
      state.scrollY -
      state.previousScrollY;

    if (Math.abs(delta) > 3) {
      const direction =
        delta > 0
          ? 'down'
          : 'up';

      if (
        state.lastScrollDirection !==
        direction
      ) {
        state.lastScrollDirection =
          direction;

        setRootDataset(
          'nbrScrollDirection',
          direction
        );
      }
    }
  }

  function requestScrollFrame() {
    if (
      state.scrollFrame !== null ||
      !state.isVisible
    ) {
      return;
    }

    state.scrollFrame =
      win.requestAnimationFrame(
        updateScrollState
      );
  }

  /* --------------------------------------------------------------------------
     Reveal observer
     -------------------------------------------------------------------------- */

  function revealImmediately(element) {
    element.classList.add(
      'is-visible'
    );

    element.dataset.nbrRevealed =
      'true';

    element.dataset.nbrObserved =
      'false';
  }

  function createRevealObserver() {
    revealObserver?.disconnect();
    revealObserver = null;

    if (
      state.reducedMotion ||
      state.quality === 'performance' ||
      !supportsIntersectionObserver()
    ) {
      return;
    }

    revealObserver =
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const element =
              entry.target;

            const repeat =
              element.hasAttribute(
                'data-nbr-reveal-repeat'
              );

            if (entry.isIntersecting) {
              element.classList.add(
                'is-visible'
              );

              element.dataset.nbrRevealed =
                'true';

              if (!repeat) {
                revealObserver.unobserve(
                  element
                );
              }

              return;
            }

            if (repeat) {
              element.classList.remove(
                'is-visible'
              );

              element.dataset.nbrRevealed =
                'false';
            }
          });
        },
        {
          threshold: 0.08,
          rootMargin:
            '0px 0px -5% 0px'
        }
      );
  }

  function initialiseRevealObserver(
    container = doc
  ) {
    getElements(
      container,
      selectors.reveal
    ).forEach((element) => {
      const repeat =
        element.hasAttribute(
          'data-nbr-reveal-repeat'
        );

      if (
        state.reducedMotion ||
        state.quality === 'performance' ||
        !revealObserver
      ) {
        revealImmediately(element);
        return;
      }

      if (
        !repeat &&
        element.dataset.nbrRevealed ===
          'true'
      ) {
        element.classList.add(
          'is-visible'
        );

        return;
      }

      if (
        observedRevealElements.has(
          element
        )
      ) {
        return;
      }

      observedRevealElements.add(
        element
      );

      element.dataset.nbrObserved =
        'true';

      revealObserver.observe(element);
    });
  }

  /* --------------------------------------------------------------------------
     Section observer
     -------------------------------------------------------------------------- */

  function createSectionObserver() {
    sectionObserver?.disconnect();
    sectionObserver = null;

    if (
      !supportsIntersectionObserver()
    ) {
      return;
    }

    sectionObserver =
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const section =
              entry.target;

            const visible =
              entry.isIntersecting;

            section.classList.toggle(
              'is-in-view',
              visible
            );

            section.dataset.nbrVisible =
              visible
                ? 'true'
                : 'false';

            section.dispatchEvent(
              new CustomEvent(
                'nbr:visibility',
                {
                  bubbles: false,
                  detail: {
                    visible,
                    ratio:
                      entry.intersectionRatio
                  }
                }
              )
            );
          });
        },
        {
          threshold: [
            0,
            0.12
          ],
          rootMargin:
            '120px 0px'
        }
      );
  }

  function initialiseSectionObserver(
    container = doc
  ) {
    getElements(
      container,
      selectors.section
    ).forEach((section) => {
      if (
        observedSectionElements.has(
          section
        )
      ) {
        return;
      }

      observedSectionElements.add(
        section
      );

      section.dataset
        .nbrSectionObserved = 'true';

      if (!sectionObserver) {
        section.classList.add(
          'is-in-view'
        );

        section.dataset.nbrVisible =
          'true';

        return;
      }

      sectionObserver.observe(section);
    });
  }

  /* --------------------------------------------------------------------------
     Video observer
     -------------------------------------------------------------------------- */

  function videoAllowsAutoplay(video) {
    return (
      video.autoplay ||
      video.hasAttribute('autoplay') ||
      video.hasAttribute(
        'data-nbr-autoplay'
      )
    );
  }

  function shouldVideoPlay(video) {
    const visible =
      video.dataset.nbrVideoVisible ===
      'true';

    const allowReducedMotion =
      video.dataset.nbrAllowMotion ===
      'true';

    const disableInPerformance =
      video.dataset
        .nbrDisableOnPerformance ===
      'true';

    return (
      visible &&
      state.isVisible &&
      videoAllowsAutoplay(video) &&
      (
        !state.reducedMotion ||
        allowReducedMotion
      ) &&
      !(
        state.quality ===
          'performance' &&
        disableInPerformance
      )
    );
  }

  function playVideo(video) {
    if (
      !shouldVideoPlay(video) ||
      !video.paused
    ) {
      return;
    }

    const playPromise =
      video.play();

    if (
      playPromise &&
      typeof playPromise.catch ===
        'function'
    ) {
      playPromise.catch(() => {
        // Autoplay can be blocked.
      });
    }
  }

  function pauseVideo(video) {
    if (!video.paused) {
      video.pause();
    }
  }

  function syncVideoPlayback(video) {
    if (shouldVideoPlay(video)) {
      playVideo(video);
      return;
    }

    pauseVideo(video);
  }

  function syncAllVideos(
    container = doc
  ) {
    getElements(
      container,
      selectors.video
    ).forEach(syncVideoPlayback);
  }

  function createVideoObserver() {
    videoObserver?.disconnect();
    videoObserver = null;

    if (
      !supportsIntersectionObserver()
    ) {
      return;
    }

    videoObserver =
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video =
              entry.target;

            video.dataset
              .nbrVideoVisible =
              entry.isIntersecting
                ? 'true'
                : 'false';

            syncVideoPlayback(video);
          });
        },
        {
          threshold: 0.08,
          rootMargin:
            '100px 0px'
        }
      );
  }

  function initialiseVideoObserver(
    container = doc
  ) {
    getElements(
      container,
      selectors.video
    ).forEach((video) => {
      if (
        observedVideoElements.has(video)
      ) {
        syncVideoPlayback(video);
        return;
      }

      observedVideoElements.add(video);

      video.dataset.nbrVideoObserved =
        'true';

      video.preload =
        video.dataset.nbrPreload ||
        (
          state.quality === 'performance'
            ? 'none'
            : video.preload || 'metadata'
        );

      if (!videoObserver) {
        video.dataset.nbrVideoVisible =
          'true';

        syncVideoPlayback(video);
        return;
      }

      videoObserver.observe(video);
    });
  }

  /* --------------------------------------------------------------------------
     Initialisation and dynamic content
     -------------------------------------------------------------------------- */

  function initialise(
    container = doc
  ) {
    if (state.destroyed) {
      return;
    }

    initialiseRevealObserver(container);
    initialiseSectionObserver(container);
    initialiseVideoObserver(container);
  }

  function teardown(container) {
    if (!container) {
      return;
    }

    getElements(
      container,
      selectors.reveal
    ).forEach((element) => {
      revealObserver?.unobserve(
        element
      );
    });

    getElements(
      container,
      selectors.section
    ).forEach((section) => {
      sectionObserver?.unobserve(
        section
      );
    });

    getElements(
      container,
      selectors.video
    ).forEach((video) => {
      videoObserver?.unobserve(video);
      pauseVideo(video);
    });
  }

  function rebuildMotionObservers() {
    createRevealObserver();
    createVideoObserver();

    initialiseRevealObserver(doc);
    initialiseVideoObserver(doc);
  }

  function processMutationRoots() {
    state.mutationFrame = null;

    const roots = Array.from(
      state.pendingMutationRoots
    );

    state.pendingMutationRoots.clear();

    roots.forEach((container) => {
      initialise(container);
    });

    requestScrollFrame();
  }

  function scheduleMutationProcessing() {
    if (
      state.mutationFrame !== null
    ) {
      return;
    }

    state.mutationFrame =
      win.requestAnimationFrame(
        processMutationRoots
      );
  }

  function createMutationObserver() {
    if (
      !('MutationObserver' in win) ||
      !doc.body
    ) {
      return;
    }

    mutationObserver =
      new MutationObserver(
        (mutations) => {
          let hasRelevantContent = false;

          mutations.forEach((mutation) => {
            mutation.addedNodes.forEach(
              (node) => {
                if (
                  !containsEngineElements(
                    node
                  )
                ) {
                  return;
                }

                state
                  .pendingMutationRoots
                  .add(node);

                hasRelevantContent = true;
              }
            );
          });

          if (hasRelevantContent) {
            scheduleMutationProcessing();
          }
        }
      );

    mutationObserver.observe(
      doc.body,
      {
        childList: true,
        subtree: true
      }
    );
  }

  /* --------------------------------------------------------------------------
     Visibility and resizing
     -------------------------------------------------------------------------- */

  function handleVisibilityChange() {
    state.isVisible = !doc.hidden;

    setRootDataset(
      'nbrPageVisible',
      state.isVisible
        ? 'true'
        : 'false'
    );

    syncAllVideos();

    if (state.isVisible) {
      requestScrollFrame();
    } else {
      cancelFrame('pointerFrame');
      cancelFrame('scrollFrame');
    }
  }

  function handleResizeFrame() {
    state.resizeFrame = null;

    const previousTouchState =
      state.isTouch;

    const previousMotionState =
      state.reducedMotion;

    const previousMobileState =
      state.isMobile;

    updateDeviceState();
    updateViewportVariables();

    if (
      previousTouchState !==
        state.isTouch ||
      previousMobileState !==
        state.isMobile
    ) {
      resetPointerPosition();
    }

    if (
      previousMotionState !==
      state.reducedMotion
    ) {
      rebuildMotionObservers();
    }

    if (
      state.qualitySource === 'auto'
    ) {
      applyQuality(
        getAutomaticQuality(),
        {
          source: 'auto'
        }
      );
    }

    requestScrollFrame();
  }

  function requestResizeFrame() {
    if (
      state.resizeFrame !== null
    ) {
      return;
    }

    state.resizeFrame =
      win.requestAnimationFrame(
        handleResizeFrame
      );
  }

  /* --------------------------------------------------------------------------
     Shopify Theme Editor
     -------------------------------------------------------------------------- */

  function bindShopifyEvents() {
    listen(
      doc,
      'shopify:section:load',
      (event) => {
        initialise(event.target);
        requestScrollFrame();
      }
    );

    listen(
      doc,
      'shopify:section:unload',
      (event) => {
        teardown(event.target);
        requestScrollFrame();
      }
    );

    listen(
      doc,
      'shopify:section:reorder',
      requestScrollFrame
    );

    listen(
      doc,
      'shopify:section:select',
      (event) => {
        initialise(event.target);

        if (event.target?.dataset) {
          event.target.dataset
            .nbrSelected = 'true';
        }
      }
    );

    listen(
      doc,
      'shopify:section:deselect',
      (event) => {
        if (event.target?.dataset) {
          event.target.dataset
            .nbrSelected = 'false';
        }
      }
    );

    listen(
      doc,
      'shopify:block:select',
      (event) => {
        initialise(event.target);

        event.target?.classList?.add(
          'is-visible'
        );
      }
    );
  }

  /* --------------------------------------------------------------------------
     Global events
     -------------------------------------------------------------------------- */

  function bindGlobalEvents() {
    listen(
      win,
      'pointermove',
      handlePointerMove,
      {
        passive: true
      }
    );

    listen(
      doc,
      'mouseleave',
      handlePointerLeave,
      {
        passive: true
      }
    );

    listen(
      win,
      'scroll',
      requestScrollFrame,
      {
        passive: true
      }
    );

    listen(
      win,
      'resize',
      requestResizeFrame,
      {
        passive: true
      }
    );

    listen(
      win,
      'orientationchange',
      requestResizeFrame,
      {
        passive: true
      }
    );

    listen(
      doc,
      'visibilitychange',
      handleVisibilityChange
    );

    listen(
      win,
      'pageshow',
      () => {
        state.isVisible = true;
        handleVisibilityChange();
        requestResizeFrame();
      },
      {
        passive: true
      }
    );

    listenToMediaQuery(
      mediaQueries.reducedMotion,
      requestResizeFrame
    );

    listenToMediaQuery(
      mediaQueries.coarsePointer,
      requestResizeFrame
    );

    listenToMediaQuery(
      mediaQueries.hoverPointer,
      requestResizeFrame
    );

    listenToMediaQuery(
      mediaQueries.mobileViewport,
      requestResizeFrame
    );
  }

  /* --------------------------------------------------------------------------
     Public controls
     -------------------------------------------------------------------------- */

  function getPublicState() {
    return {
      version: ENGINE_VERSION,
      quality: state.quality,
      qualitySource:
        state.qualitySource,
      isTouch: state.isTouch,
      canHover: state.canHover,
      isMobile: state.isMobile,
      reducedMotion:
        state.reducedMotion,
      isVisible: state.isVisible,
      scrollY: state.scrollY,
      scrollProgress:
        state.scrollProgress,
      started: state.started
    };
  }

  function exposeEngineControls() {
    namespace.setQuality = (mode) => {
      if (mode === 'auto') {
        namespace.resetQuality();
        return true;
      }

      return applyQuality(
        mode,
        {
          persist: true,
          source: 'manual'
        }
      );
    };

    namespace.resetQuality = () => {
      safeStorageRemove(STORAGE_KEY);

      applyQuality(
        getAutomaticQuality(),
        {
          source: 'auto'
        }
      );
    };

    namespace.getQuality = () => ({
      mode: state.quality,
      source: state.qualitySource
    });

    namespace.refresh = (
      container = doc
    ) => {
      initialise(container);
      requestScrollFrame();
    };

    namespace.pauseVideos = (
      container = doc
    ) => {
      getElements(
        container,
        selectors.video
      ).forEach(pauseVideo);
    };

    namespace.resumeVideos = (
      container = doc
    ) => {
      syncAllVideos(container);
    };

    namespace.getState =
      getPublicState;

    engineApi = {
      version: ENGINE_VERSION,
      refresh: namespace.refresh,
      destroy,
      getState: getPublicState,
      setQuality:
        namespace.setQuality,
      resetQuality:
        namespace.resetQuality
    };

    namespace.engine = engineApi;
  }

  /* --------------------------------------------------------------------------
     Start and destroy
     -------------------------------------------------------------------------- */

  function destroy() {
    if (state.destroyed) {
      return;
    }

    state.destroyed = true;
    state.started = false;

    cancelFrame('pointerFrame');
    cancelFrame('scrollFrame');
    cancelFrame('resizeFrame');
    cancelFrame('mutationFrame');
    cancelIdleTask();

    revealObserver?.disconnect();
    sectionObserver?.disconnect();
    videoObserver?.disconnect();
    mutationObserver?.disconnect();

    revealObserver = null;
    sectionObserver = null;
    videoObserver = null;
    mutationObserver = null;

    while (cleanupTasks.length) {
      const cleanup =
        cleanupTasks.pop();

      try {
        cleanup();
      } catch (error) {
        // Safe during Theme Editor reloads.
      }
    }

    state.pendingMutationRoots.clear();

    setRootDataset(
      'nbrReady',
      'false'
    );

    if (
      namespace.engine === engineApi
    ) {
      delete namespace.engine;
    }
  }

  function start() {
    if (
      state.started ||
      state.destroyed
    ) {
      return;
    }

    state.started = true;

    updateDeviceState();
    updateViewportVariables();
    detectQualityMode();
    resetPointerPosition();

    createRevealObserver();
    createSectionObserver();
    createVideoObserver();

    exposeEngineControls();
    bindGlobalEvents();
    bindShopifyEvents();

    /*
     * Initialise existing content immediately.
     * Mutation monitoring starts during idle time
     * so it does not compete with first paint.
     */
    initialise(doc);

    runWhenIdle(() => {
      if (!state.destroyed) {
        createMutationObserver();
      }
    });

    handleVisibilityChange();
    requestScrollFrame();

    setRootDataset(
      'nbrReady',
      'true'
    );

    dispatch(
      'nbr:ready',
      {
        version: ENGINE_VERSION,
        quality: state.quality,
        isTouch: state.isTouch,
        isMobile: state.isMobile,
        reducedMotion:
          state.reducedMotion
      }
    );
  }

  if (
    doc.readyState === 'loading'
  ) {
    listen(
      doc,
      'DOMContentLoaded',
      start,
      {
        once: true
      }
    );
  } else {
    start();
  }
})();